'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Check, Zap, Building2, Crown, ArrowRight, Shield, Star } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';


interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  max_users: number;
  max_invoices_per_month: number;
  features: string[];
  stripe_price_id_monthly: string | null;
  stripe_price_id_yearly: string | null;
}

const planIcons: Record<string, React.ElementType> = {
  starter: Zap,
  growth: Building2,
  enterprise: Crown,
};

const planAccents: Record<string, string> = {
  starter: 'border-slate-200',
  growth: 'border-primary ring-2 ring-primary/20',
  enterprise: 'border-slate-200',
};

const planBadge: Record<string, string | null> = {
  starter: null,
  growth: 'Most Popular',
  enterprise: null,
};

export default function PricingPage() {
  const router = useRouter();
  const { user, company } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    const fetchPlans = async () => {
      const { data } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (data) {
        setPlans(data.map((p: any) => ({ ...p, features: Array.isArray(p.features) ? p.features : JSON.parse(p.features || '[]') })));
      }
      setLoading(false);
    };
    fetchPlans();
  }, []);

  const handleSelectPlan = async (plan: Plan) => {
    if (!user) {
      router.push(`/sign-up-login-screen?plan=${plan.slug}`);
      return;
    }
    setCheckoutLoading(plan.slug);
    try {
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://ledgerpay6559.builtwithrocket.new';
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: {
          planSlug: plan.slug,
          billingInterval: billing,
          companyId: company?.id,
          successUrl: `${siteUrl}/onboarding?plan=${plan.slug}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${siteUrl}/pricing`,
        },
      });
      if (error || !data?.url) throw new Error(data?.error || 'Failed to create checkout session');
      window.location.href = data.url;
    } catch (err: any) {
      console.error('Checkout error:', err);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const yearlyDiscount = (monthly: number, yearly: number) => {
    const annualMonthly = monthly * 12;
    return Math.round(((annualMonthly - yearly) / annualMonthly) * 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-base text-foreground">LedgerPay</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link href="/" className="text-sm font-medium text-primary hover:underline">
                Go to Dashboard →
              </Link>
            ) : (
              <>
                <Link href="/sign-up-login-screen" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                  Sign in
                </Link>
                <Link href="/sign-up-login-screen" className="text-sm font-semibold bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-primary/8 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-5 border border-primary/20">
            <Shield size={12} />
            SOC 2 Ready · Multi-Tenant · Enterprise Grade
          </div>
          <h1 className="text-4xl font-bold text-foreground tracking-tight mb-4">
            Transparent pricing for finance teams
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Start with a 14-day free trial. No credit card required. Upgrade or cancel anytime.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-1 bg-muted rounded-xl p-1 mt-8">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${billing === 'monthly' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center gap-2 ${billing === 'yearly' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Yearly
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">Save 20%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className="rounded-2xl border border-border bg-card animate-pulse h-[520px]" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const Icon = planIcons[plan.slug] || Zap;
              const isPopular = planBadge[plan.slug];
              const price = billing === 'monthly' ? plan.price_monthly : plan.price_yearly / 12;
              const discount = yearlyDiscount(plan.price_monthly, plan.price_yearly);
              const isCurrentPlan = company?.plan === plan.slug;

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border bg-card p-7 flex flex-col transition-all duration-200 hover:shadow-lg ${planAccents[plan.slug] || 'border-border'}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm">
                        <Star size={10} fill="currentColor" />
                        {isPopular}
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${plan.slug === 'growth' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                      <Icon size={20} />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{plan.description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-foreground">${price.toFixed(0)}</span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    {billing === 'yearly' && (
                      <p className="text-xs text-emerald-600 font-medium mt-1">
                        ${plan.price_yearly}/yr · Save {discount}% vs monthly
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Up to {plan.max_users === 100 ? 'unlimited' : plan.max_users} users ·{' '}
                      {plan.max_invoices_per_month >= 999999 ? 'Unlimited' : plan.max_invoices_per_month.toLocaleString()} invoices/mo
                    </p>
                  </div>

                  <ul className="space-y-2.5 flex-1 mb-7">
                    {plan.features.map((feature, idx) => (
                      <li key={`feat-${plan.slug}-${idx}`} className="flex items-start gap-2.5 text-sm text-foreground">
                        <Check size={15} className="text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={!!checkoutLoading || isCurrentPlan}
                    className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${
                      plan.slug === 'growth' ?'bg-primary text-primary-foreground hover:bg-primary/90' :'bg-muted text-foreground hover:bg-muted/80 border border-border'
                    }`}
                  >
                    {checkoutLoading === plan.slug ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Redirecting...
                      </span>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      <>
                        {user ? 'Upgrade Now' : 'Start Free Trial'}
                        <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Trust bar */}
        <div className="mt-16 pt-10 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { label: 'Uptime SLA', value: '99.9%' },
              { label: 'Tenants Onboarded', value: '500+' },
              { label: 'Invoices Processed', value: '$2.4B+' },
              { label: 'Support Response', value: '< 4 hrs' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className="mt-12 rounded-2xl bg-primary/5 border border-primary/15 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-lg font-bold text-foreground">Need a custom enterprise contract?</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Volume discounts, custom SLAs, dedicated infrastructure, and white-glove onboarding available.
            </p>
          </div>
          <a
            href="mailto:enterprise@ledgerpay.io"
            className="flex-shrink-0 bg-primary text-primary-foreground text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            Contact Sales <ArrowRight size={15} />
          </a>
        </div>

        {/* Legal Footer */}
        <div className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">© 2026 LedgerPay. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs">
            <Link href="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
            <Link href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
